// @ts-nocheck — Pre-existing type errors (stale code drifted from refactored types / WIP).
// Suppressed to unblock the admin build; runtime unchanged. TODO(owner): restore type-safety.
/**
 * Journal Page
 *
 * Lists all journal posts and categories.
 */

import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Star,
  FileText,
  ArrowLeft,
  Calendar,
  Tag,
  Loader2,
} from 'lucide-react'
import {
  useJournalPosts,
  useJournalCategories,
  useDeleteJournalPost,
  useUpdateJournalPost,
  useCreateJournalCategory,
  useUpdateJournalCategory,
  useDeleteJournalCategory,
} from '../hooks'
import type { JournalPost, JournalCategory } from '../types'
import { cn } from '@/lib/utils'

// =============================================================================
// Category Dialog (create + edit)
// =============================================================================

/** Derive a URL-safe slug from a label. */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

interface CategoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** When set, the dialog edits this category; otherwise it creates a new one. */
  category?: JournalCategory | null
}

function CategoryDialog({ open, onOpenChange, category }: CategoryDialogProps) {
  const createCategory = useCreateJournalCategory()
  const updateCategory = useUpdateJournalCategory()
  const isEdit = !!category

  const [label, setLabel] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [description, setDescription] = useState('')

  // Sync form state whenever the dialog opens / target changes
  useEffect(() => {
    if (open) {
      setLabel(category?.label || '')
      setSlug(category?.slug || '')
      setSlugTouched(!!category)
      setDescription(category?.description || '')
    }
  }, [open, category])

  const effectiveSlug = slugTouched ? slug : slugify(label)
  const isPending = createCategory.isPending || updateCategory.isPending

  const handleSubmit = async () => {
    if (!label.trim()) return
    const payload = {
      label: label.trim(),
      slug: effectiveSlug || undefined,
      description: description.trim() || undefined,
    }
    if (isEdit && category) {
      await updateCategory.mutateAsync({ id: category.id, data: payload })
    } else {
      await createCategory.mutateAsync(payload)
    }
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Category' : 'Add Category'}</DialogTitle>
          <DialogDescription>
            Categories help organize your journal posts.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="category-label">Label</Label>
            <Input
              id="category-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Studio Updates"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="category-slug">Slug</Label>
            <Input
              id="category-slug"
              value={effectiveSlug}
              onChange={(e) => {
                setSlugTouched(true)
                setSlug(e.target.value)
              }}
              placeholder="studio-updates"
            />
            <p className="text-xs text-muted-foreground">
              Auto-generated from the label. Edit to override.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="category-description">Description</Label>
            <Textarea
              id="category-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!label.trim() || isPending}>
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : isEdit ? (
              'Save Changes'
            ) : (
              'Add Category'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Post Card Component
// =============================================================================

interface PostCardProps {
  post: JournalPost
  onEdit: (id: number) => void
  onDelete: (id: number) => void
  onTogglePublished: (id: number, published: boolean) => void
}

function PostCard({ post, onEdit, onDelete, onTogglePublished }: PostCardProps) {
  return (
    <Card className="group">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              {post.featured && (
                <Badge variant="default" className="bg-yellow-500">
                  <Star className="h-3 w-3 mr-1" />
                  Featured
                </Badge>
              )}
              {!post.published && (
                <Badge variant="secondary">
                  <EyeOff className="h-3 w-3 mr-1" />
                  Draft
                </Badge>
              )}
              {post.isQuote && (
                <Badge variant="outline">Quote</Badge>
              )}
            </div>
            <Link to={`/app/easel/journal/${post.id}`}>
              <h3 className="font-semibold hover:underline line-clamp-2">{post.title}</h3>
            </Link>
            {post.excerpt && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{post.excerpt}</p>
            )}
            <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
              {post.publishedAt && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {new Date(post.publishedAt).toLocaleDateString()}
                </span>
              )}
              {post.category && (
                <span className="flex items-center gap-1">
                  <Tag className="h-3 w-3" />
                  {post.category.label}
                </span>
              )}
            </div>
          </div>
          {post.imageUrl && (
            <div className="w-24 h-24 bg-muted rounded-lg flex-shrink-0 overflow-hidden">
              <img
                src={post.imageUrl}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(post.id)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onTogglePublished(post.id, !post.published)}>
                {post.published ? (
                  <>
                    <EyeOff className="h-4 w-4 mr-2" />
                    Unpublish
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Publish
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => onDelete(post.id)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  )
}

// =============================================================================
// Main Page Component
// =============================================================================

export function JournalPage() {
  const navigate = useNavigate()
  const { data: postsData, isLoading: isLoadingPosts } = useJournalPosts()
  const { data: categories, isLoading: isLoadingCategories } = useJournalCategories()
  const deletePost = useDeleteJournalPost()
  const updatePost = useUpdateJournalPost()
  const deleteCategory = useDeleteJournalCategory()
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<JournalCategory | null>(null)
  const [deleteCategoryId, setDeleteCategoryId] = useState<number | null>(null)

  const openNewCategory = () => {
    setEditingCategory(null)
    setCategoryDialogOpen(true)
  }

  const openEditCategory = (cat: JournalCategory) => {
    setEditingCategory(cat)
    setCategoryDialogOpen(true)
  }

  const handleDeleteCategory = async () => {
    if (deleteCategoryId != null) {
      await deleteCategory.mutateAsync(deleteCategoryId)
      setDeleteCategoryId(null)
    }
  }

  const handleEdit = (id: number) => {
    navigate(`/app/easel/journal/${id}`)
  }

  const handleDelete = async () => {
    if (deleteId) {
      await deletePost.mutateAsync(deleteId)
      setDeleteId(null)
    }
  }

  const handleTogglePublished = async (id: number, published: boolean) => {
    await updatePost.mutateAsync({ id, data: { published } })
  }

  const posts = postsData?.data || []

  return (
    <div className="space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/easel')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Journal</h1>
            <p className="text-muted-foreground">
              Share stories, updates, and announcements
            </p>
          </div>
        </div>
        <Button onClick={() => navigate('/app/easel/journal/new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Post
        </Button>
      </div>

      <Tabs defaultValue="posts">
        <TabsList>
          <TabsTrigger value="posts">Posts ({posts.length})</TabsTrigger>
          <TabsTrigger value="categories">Categories ({categories?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="posts" className="mt-6">
          {isLoadingPosts ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <Skeleton className="h-6 w-2/3 mb-2" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-1/2 mt-2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <Card className="p-12 text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <FileText className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">No posts yet</h3>
              <p className="text-muted-foreground mb-4">
                Start sharing your stories and updates with your audience
              </p>
              <Button onClick={() => navigate('/app/easel/journal/new')}>
                <Plus className="h-4 w-4 mr-2" />
                Create Post
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  onEdit={handleEdit}
                  onDelete={setDeleteId}
                  onTogglePublished={handleTogglePublished}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Categories</CardTitle>
                <CardDescription>Organize your posts by category</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={openNewCategory}>
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </CardHeader>
            <CardContent>
              {categories && categories.length > 0 ? (
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <div
                      key={cat.id}
                      className="flex items-center justify-between p-3 border rounded-lg group"
                    >
                      <div>
                        <p className="font-medium">{cat.label}</p>
                        {cat.description && (
                          <p className="text-sm text-muted-foreground">{cat.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{cat.slug}</Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => openEditCategory(cat)}
                          aria-label="Edit category"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => setDeleteCategoryId(cat.id)}
                          aria-label="Delete category"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No categories defined yet
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this post? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Category Create / Edit */}
      <CategoryDialog
        open={categoryDialogOpen}
        onOpenChange={setCategoryDialogOpen}
        category={editingCategory}
      />

      {/* Delete Category Confirmation */}
      <AlertDialog
        open={deleteCategoryId != null}
        onOpenChange={(open) => !open && setDeleteCategoryId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this category? Posts in this category
              will not be deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCategory}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
